// GA4 accepts string or numeric transaction IDs and prefers explicit
// gtag_reference fields.
const getOrderReferenceField = (
  value: unknown,
): string | number | undefined => {
  if (typeof value === "number") {
    return value;
  }

  return typeof value === "string" && value !== "" ? value : undefined;
};

const getNumericOrderReferenceField = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value !== "string" || value === "") {
    return undefined;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const getNonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

const asOrderReferenceRecord = (
  value: unknown,
): Record<string, unknown> | undefined =>
  value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;

export const hasNullOrderPayloadEnvelope = (backendResponse: unknown) => {
  const response = asOrderReferenceRecord(backendResponse);

  return (
    response !== undefined &&
    Object.prototype.hasOwnProperty.call(response, "payload") &&
    response.payload === null
  );
};

const getTrackingProof = (value: unknown) => {
  const tracking = asOrderReferenceRecord(value);
  const orderTrackingUrl = getNonEmptyString(tracking?.order_tracking_url);
  const googleAdsConversionEventUrl = getNonEmptyString(
    tracking?.google_ads_conversion_event_url,
  );
  const expiresAt = getNonEmptyString(tracking?.expires_at);

  if (!orderTrackingUrl && !googleAdsConversionEventUrl) {
    return undefined;
  }

  return {
    ...(orderTrackingUrl ? { order_tracking_url: orderTrackingUrl } : {}),
    ...(googleAdsConversionEventUrl
      ? { google_ads_conversion_event_url: googleAdsConversionEventUrl }
      : {}),
    ...(expiresAt ? { expires_at: expiresAt } : {}),
  };
};

export const resolveOrderTrackingProof = (backendResponse: unknown) => {
  const response = asOrderReferenceRecord(backendResponse);
  const responsePayload = asOrderReferenceRecord(response?.payload);
  const responsePayloadData = asOrderReferenceRecord(responsePayload?.data);
  const responseData = asOrderReferenceRecord(response?.data);

  return (
    getTrackingProof(responsePayloadData?.tracking) ??
    getTrackingProof(responsePayload?.tracking) ??
    getTrackingProof(responseData?.tracking) ??
    getTrackingProof(response?.tracking)
  );
};

/**
 * Use for GA4 transaction IDs. Prefer explicit gtag references, then backend
 * order IDs, while respecting payload envelopes from order-create responses.
 * If a payload envelope exists, even empty/null, do not fall back to an
 * unrelated outer response id.
 */
export const resolveOrderReference = (
  backendResponse: unknown,
): string | number | undefined => {
  const response = asOrderReferenceRecord(backendResponse);
  const responsePayload = asOrderReferenceRecord(response?.payload);
  const responsePayloadData = asOrderReferenceRecord(responsePayload?.data);

  return (
    // GA4 transaction IDs prefer the explicit gtag reference when present.
    getOrderReferenceField(responsePayloadData?.gtag_reference) ??
    getOrderReferenceField(responsePayload?.gtag_reference) ??
    getOrderReferenceField(response?.gtag_reference) ??
    getOrderReferenceField(responsePayloadData?.id) ??
    getOrderReferenceField(responsePayload?.id) ??
    // Some order endpoints return the order directly. If a payload envelope is
    // present, even as an empty object or null, avoid falling back to an
    // unrelated outer response id. The strict undefined check intentionally
    // treats null as a present-but-empty payload envelope.
    (response?.payload === undefined
      ? getOrderReferenceField(response?.id)
      : undefined)
  );
};

/**
 * Trustpilot still uses the numeric order ID for its authenticated account
 * connect flow. Tracking endpoints must use resolveOrderTrackingProof instead.
 */
export const resolveNumericOrderReference = (
  backendResponse: unknown,
): number | undefined => {
  const response = asOrderReferenceRecord(backendResponse);
  const responsePayload = asOrderReferenceRecord(response?.payload);
  const responsePayloadData = asOrderReferenceRecord(responsePayload?.data);

  return (
    // Keep numeric legacy fallbacks for the separate Trustpilot connect flow.
    getNumericOrderReferenceField(responsePayloadData?.id) ??
    getNumericOrderReferenceField(responsePayload?.id) ??
    getNumericOrderReferenceField(response?.id) ??
    getNumericOrderReferenceField(responsePayloadData?.gtag_reference) ??
    getNumericOrderReferenceField(responsePayload?.gtag_reference) ??
    getNumericOrderReferenceField(response?.gtag_reference)
  );
};
